import { Link, NavLink, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { COUNTRY_LIST } from '../data/countries';

const links = [
  { to: '/uae', label: 'UAE' },
  { to: '/usa', label: 'USA' },
  { to: '/uk', label: 'UK' },
  { to: '/germany', label: 'DE' },
  { to: '/france', label: 'FR' },
  { to: '/spain', label: 'ES' },
  { to: '/switzerland', label: 'CH' },
  { to: '/netherlands', label: 'NL' },
  { to: '/greece', label: 'GR' },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled ? 'bg-black/70 backdrop-blur-xl border-b border-white/10' : 'bg-transparent'
      }`}
      data-testid="site-nav"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="group flex items-baseline gap-3" data-testid="nav-logo">
          <span className="font-serif text-xl md:text-2xl tracking-tight font-light text-white">Belliardo</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 hidden md:inline">
            International practice · Verona
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `font-mono text-[11px] uppercase tracking-[0.25em] hover-underline ${
                  isActive ? 'text-white' : 'text-white/60 hover:text-white'
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <NavLink to="/articles" data-testid="nav-link-journal"
            className={({ isActive }) => `font-mono text-[11px] uppercase tracking-[0.25em] hover-underline pl-6 border-l border-white/15 ${isActive ? 'text-white' : 'text-white/60 hover:text-white'}`}>
            Journal
          </NavLink>
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="#claim-form"
            data-testid="nav-cta-submit"
            className="hidden md:inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-white text-[11px] font-mono uppercase tracking-[0.2em] hover:bg-white hover:text-black transition-colors duration-300"
          >
            Submit a Claim
          </a>
          <button
            aria-label="Toggle menu"
            data-testid="nav-menu-toggle"
            className="lg:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="h-5 w-5" strokeWidth={1.25} /> : <Menu className="h-5 w-5" strokeWidth={1.25} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-black/95 backdrop-blur-xl border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4" data-testid="nav-mobile-menu">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className="font-mono text-xs uppercase tracking-[0.25em] text-white/80"
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </NavLink>
              ))}
              <a
                href="#claim-form"
                className="mt-2 inline-flex items-center gap-2 px-4 py-3 border border-white/20 text-white text-[11px] font-mono uppercase tracking-[0.2em] w-fit"
                data-testid="nav-mobile-cta"
              >
                Submit a Claim
              </a>
              <NavLink to="/articles" className="font-mono text-xs uppercase tracking-[0.25em] text-white/80" data-testid="nav-mobile-link-journal">
                Journal
              </NavLink>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
